/**
 * SignPage — Public e-signature page.
 *
 * Accessible at /sign/:token (no authentication required).
 * The token is a UUID stored in documents.signing_token.
 *
 * Flow:
 *   1. Load document by signing_token via anon Supabase query.
 *   2. Tenant enters their name and draws a signature.
 *   3. On submit:
 *      a. INSERT into document_signatures (with base64 signature data URL).
 *      b. UPDATE documents SET status='signed', signed_at=now().
 *   4. Show success screen.
 *
 * Security:
 *   - Token is a UUID (hard to guess).
 *   - RLS policies allow anon read/update only for documents with a valid token.
 *   - Signature data stored as base64 PNG in document_signatures.signature_path
 *     (can be migrated to storage later without changing the flow).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Check, Loader2, X, RotateCcw, PenLine } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { formatDate } from '../utils/formatters';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DocumentInfo {
    id: string;
    name: string;
    type: string;
    file_url: string | null;
    status: string;
    signing_token: string;
    signed_at: string | null;
    property: { name: string; address?: string } | null;
    tenant_link: { tenant_name: string; tenant_email: string } | null;
}

// ─── Signature Canvas ─────────────────────────────────────────────────────────

interface SignaturePadProps {
    onChange: (dataUrl: string | null) => void;
}

function SignaturePad({ onChange }: SignaturePadProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isDrawing = useRef(false);
    const [isEmpty, setIsEmpty] = useState(true);

    // Initialize canvas context
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#1a1d2e';
    }, []);

    const getPoint = (canvas: HTMLCanvasElement, e: MouseEvent | Touch): { x: number; y: number } => {
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const clientX = 'clientX' in e ? e.clientX : (e as Touch).clientX;
        const clientY = 'clientY' in e ? e.clientY : (e as Touch).clientY;
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY,
        };
    };

    const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        isDrawing.current = true;
        setIsEmpty(false);
        const raw = 'touches' in e ? e.touches[0] : (e as React.MouseEvent).nativeEvent;
        const { x, y } = getPoint(canvas, raw as MouseEvent | Touch);
        ctx.beginPath();
        ctx.moveTo(x, y);
    }, []);

    const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        if (!isDrawing.current) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const raw = 'touches' in e ? e.touches[0] : (e as React.MouseEvent).nativeEvent;
        const { x, y } = getPoint(canvas, raw as MouseEvent | Touch);
        ctx.lineTo(x, y);
        ctx.stroke();
    }, []);

    const stopDrawing = useCallback(() => {
        if (!isDrawing.current) return;
        isDrawing.current = false;
        const canvas = canvasRef.current;
        if (!canvas) return;
        onChange(canvas.toDataURL('image/png'));
    }, [onChange]);

    const clear = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setIsEmpty(true);
        onChange(null);
    };

    return (
        <div style={{ position: 'relative', userSelect: 'none' }}>
            <canvas
                ref={canvasRef}
                width={580}
                height={180}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                style={{
                    width: '100%',
                    height: '180px',
                    border: `2px solid ${isEmpty ? '#e2e8f0' : '#6366f1'}`,
                    borderRadius: '12px',
                    cursor: 'crosshair',
                    background: '#fafafa',
                    touchAction: 'none',
                    display: 'block',
                    transition: 'border-color 0.2s',
                }}
            />

            {/* Placeholder text */}
            {isEmpty && (
                <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#cbd5e1', fontSize: '0.9rem', fontWeight: 500,
                    pointerEvents: 'none',
                    display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                    <PenLine size={16} /> Draw your signature here
                </div>
            )}

            {/* Clear button */}
            {!isEmpty && (
                <button
                    type="button"
                    onClick={clear}
                    style={{
                        position: 'absolute', top: '10px', right: '10px',
                        background: 'white', border: '1px solid #e2e8f0',
                        borderRadius: '8px', padding: '5px 10px',
                        cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
                        color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    }}
                >
                    <RotateCcw size={12} /> Clear
                </button>
            )}
        </div>
    );
}

// ─── Page shell styles ─────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #eff2ff 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    fontFamily: 'inherit',
};

const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: '24px',
    padding: '3rem',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04), 0 20px 40px -8px rgba(0,0,0,0.1)',
    maxWidth: 640,
    width: '100%',
    animation: 'fadeIn 0.35s ease-out',
};

const DOC_TYPE_LABELS: Record<string, string> = {
    lease_agreement: 'Lease Agreement',
    handover_protocol: 'Handover Protocol',
    notice: 'Notice',
    custom: 'Document',
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SignPage() {
    const { token } = useParams<{ token: string }>();

    const [doc, setDoc] = useState<DocumentInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [alreadySigned, setAlreadySigned] = useState(false);

    const [signerName, setSignerName] = useState('');
    const [signatureData, setSignatureData] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDone, setIsDone] = useState(false);

    // ─── Load document ─────────────────────────────────────────────────────

    useEffect(() => {
        const load = async () => {
            if (!token) {
                setError('Invalid signing link.');
                setLoading(false);
                return;
            }

            try {
                const { data, error: qErr } = await supabase
                    .from('documents')
                    .select(`
                        id, name, type, file_url, status, signing_token, signed_at,
                        property:properties(name, address),
                        tenant_link:tenant_links(tenant_name, tenant_email)
                    `)
                    .eq('signing_token', token)
                    .maybeSingle();

                if (qErr) throw qErr;

                if (!data) {
                    setError('This signing link is invalid or has expired.');
                    setLoading(false);
                    return;
                }

                setDoc(data as unknown as DocumentInfo);

                if (data.status === 'signed') {
                    setAlreadySigned(true);
                } else if (data.status !== 'sent') {
                    setError('This document is not available for signing.');
                }
            } catch (err) {
                console.error('[SignPage] load error:', err);
                setError('Failed to load document. Please try again or contact your landlord.');
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [token]);

    // ─── Submit signature ──────────────────────────────────────────────────

    const handleSubmit = async () => {
        if (!signatureData || !signerName.trim() || !doc) return;
        setIsSubmitting(true);

        try {
            const now = new Date().toISOString();

            // 1. Record the signature
            const { error: sigErr } = await supabase.from('document_signatures').insert({
                document_id: doc.id,
                signer_name: signerName.trim(),
                signature_path: signatureData,   // base64 PNG data URL
                signed_at: now,
                created_at: now,
            });
            if (sigErr) {
                console.error('[SignPage] signature insert error:', sigErr);
                throw new Error(sigErr.message || 'Failed to save signature record');
            }

            // 2. Mark document as signed
            const { error: docErr } = await supabase
                .from('documents')
                .update({ status: 'signed', signed_at: now, updated_at: now })
                .eq('signing_token', token);
            if (docErr) {
                console.error('[SignPage] document update error:', docErr);
                throw new Error(docErr.message || 'Failed to update document status');
            }

            // Update local doc state so success screen shows correct date
            setDoc((prev) => prev ? { ...prev, status: 'signed', signed_at: now } : prev);
            setIsDone(true);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : (err as any)?.message ?? 'Please check your connection and try again.';
            console.error('[SignPage] submit error:', err);
            alert('Signing failed: ' + message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ─── Render helpers ────────────────────────────────────────────────────

    if (loading) {
        return (
            <div style={pageStyle}>
                <div style={{ ...cardStyle, textAlign: 'center' }}>
                    <Loader2 size={40} className="spin" color="#6366f1" style={{ margin: '0 auto 1.5rem' }} />
                    <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>Loading document…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={pageStyle}>
                <div style={{ ...cardStyle, textAlign: 'center' }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: '50%',
                        background: 'rgba(239,68,68,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem',
                    }}>
                        <X size={28} color="#ef4444" />
                    </div>
                    <h2 style={{ color: '#1a1d2e', margin: '0 0 0.75rem' }}>Link Invalid</h2>
                    <p style={{ color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.6 }}>{error}</p>
                </div>
            </div>
        );
    }

    if (alreadySigned || isDone) {
        return (
            <div style={pageStyle}>
                <div style={{ ...cardStyle, textAlign: 'center' }}>
                    <div style={{
                        width: 72, height: 72, borderRadius: '50%',
                        background: 'rgba(34,197,94,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem',
                    }}>
                        <Check size={36} color="#16a34a" />
                    </div>
                    <h2 style={{ color: '#1a1d2e', margin: '0 0 0.75rem' }}>Document Signed</h2>
                    <p style={{ color: 'var(--color-text-secondary)', margin: '0 0 2rem', lineHeight: 1.6 }}>
                        <strong>{doc?.name}</strong> has been signed
                        {doc?.signed_at ? ` on ${formatDate(doc.signed_at)}` : ''}.
                        A record has been created.
                    </p>
                    <div style={{
                        padding: '1rem 1.5rem',
                        background: 'rgba(34,197,94,0.07)',
                        borderRadius: '12px',
                        border: '1px solid rgba(34,197,94,0.2)',
                        color: '#16a34a', fontSize: '0.875rem', fontWeight: 600,
                        marginBottom: '1.5rem',
                    }}>
                        Thank you for signing. Your document has been recorded.
                    </div>
                    <a
                        href="/app/tenant/dashboard"
                        style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            gap: '8px', width: '100%', padding: '1rem',
                            borderRadius: '14px', border: 'none', cursor: 'pointer',
                            fontWeight: 700, fontSize: '1rem', textDecoration: 'none',
                            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                            color: '#fff',
                            boxShadow: '0 10px 20px -5px rgba(79,70,229,0.35)',
                        }}
                    >
                        ← Back to My Dashboard
                    </a>
                </div>
            </div>
        );
    }

    // ─── Main signing form ─────────────────────────────────────────────────

    return (
        <div style={pageStyle}>
            <div style={{ ...cardStyle, textAlign: 'left' }}>

                {/* ── Branded header ── */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    marginBottom: '2rem', paddingBottom: '1.5rem',
                    borderBottom: '1px solid var(--color-border-subtle)',
                }}>
                    <img
                        src="/rentnova-mark.png"
                        alt="RentNova"
                        width={40}
                        height={40}
                        style={{ display: 'block', objectFit: 'contain', flexShrink: 0, transform: 'translateY(-5px)' }}
                    />
                    <div>
                        <div style={{ fontWeight: 800, color: '#1a1d2e', fontSize: '1.125rem', lineHeight: 1.2 }}>RentNova</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>E-Signature Request</div>
                    </div>
                </div>

                {/* ── Document info ── */}
                <div style={{ marginBottom: '2rem' }}>
                    <div style={{
                        display: 'inline-flex', padding: '8px', borderRadius: '10px',
                        background: 'rgba(99,102,241,0.08)', color: '#6366f1', marginBottom: '1rem',
                    }}>
                        <FileText size={20} />
                    </div>
                    <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.625rem', fontWeight: 900, color: '#1a1d2e', letterSpacing: '-0.03em' }}>
                        {doc?.name}
                    </h2>
                    <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                        {DOC_TYPE_LABELS[doc?.type ?? 'custom'] ?? 'Document'}
                    </p>
                </div>

                {/* ── Context cards ── */}
                {(doc?.property || doc?.tenant_link) && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: '12px', marginBottom: '2rem' }}>
                        {doc.property && (
                            <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Property</div>
                                <div style={{ fontWeight: 700, color: '#1a1d2e', fontSize: '0.9375rem' }}>{doc.property.name}</div>
                                {doc.property.address && (
                                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginTop: '2px' }}>{doc.property.address}</div>
                                )}
                            </div>
                        )}
                        {doc.tenant_link && (
                            <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Tenant</div>
                                <div style={{ fontWeight: 700, color: '#1a1d2e', fontSize: '0.9375rem' }}>{doc.tenant_link.tenant_name}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginTop: '2px' }}>{doc.tenant_link.tenant_email}</div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── View file prompt ── */}
                {doc?.file_url && (
                    <div style={{
                        marginBottom: '2rem', padding: '1rem 1.5rem',
                        background: 'rgba(99,102,241,0.04)', borderRadius: '12px',
                        border: '1px solid rgba(99,102,241,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
                    }}>
                        <div>
                            <div style={{ fontWeight: 700, color: '#1a1d2e', marginBottom: '2px' }}>Review before signing</div>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                                Read the document fully before placing your signature below.
                            </div>
                        </div>
                        <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                borderRadius: '10px', padding: '0.75rem 1.25rem', fontWeight: 700,
                                textDecoration: 'none', flexShrink: 0,
                                background: '#fff', border: '1px solid #e2e8f0', color: '#1a1d2e',
                                fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '6px',
                            }}
                        >
                            Open PDF
                        </a>
                    </div>
                )}

                {/* ── Divider ── */}
                <div style={{ borderTop: '1px solid var(--color-border-subtle)', margin: '0 0 2rem' }} />

                {/* ── Name field ── */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                        fontSize: '0.8125rem', fontWeight: 800, display: 'block', marginBottom: '8px',
                        color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>
                        Your Full Name
                    </label>
                    <input
                        type="text"
                        placeholder="Enter your full legal name"
                        value={signerName}
                        onChange={(e) => setSignerName(e.target.value)}
                        style={{
                            borderRadius: '12px', padding: '1rem 1.25rem',
                            border: '1px solid #e2e8f0', fontSize: '1rem',
                            width: '100%', background: '#f8fafc', fontWeight: 600,
                            color: '#1a1d2e', outline: 'none', boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* ── Signature canvas ── */}
                <div style={{ marginBottom: '2rem' }}>
                    <label style={{
                        fontSize: '0.8125rem', fontWeight: 800, display: 'block', marginBottom: '8px',
                        color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>
                        Signature
                    </label>
                    <SignaturePad onChange={setSignatureData} />
                    <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                        Draw your signature above using your mouse or touchscreen.
                    </p>
                </div>

                {/* ── Legal consent ── */}
                <p style={{
                    fontSize: '0.8125rem', color: 'var(--color-text-tertiary)',
                    marginBottom: '1.5rem', lineHeight: 1.65, padding: '1rem',
                    background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0',
                }}>
                    By clicking <strong>Sign Document</strong>, I confirm that I have read the document above
                    and agree that my electronic signature is the legal equivalent of my handwritten signature.
                </p>

                {/* ── Submit button ── */}
                <button
                    onClick={handleSubmit}
                    disabled={!signatureData || !signerName.trim() || isSubmitting}
                    style={{
                        width: '100%', padding: '1.125rem', borderRadius: '14px',
                        border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '1rem',
                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        color: '#fff',
                        boxShadow: '0 10px 20px -5px rgba(79,70,229,0.4)',
                        opacity: (!signatureData || !signerName.trim() || isSubmitting) ? 0.5 : 1,
                        transition: 'opacity 0.2s',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    }}
                >
                    {isSubmitting
                        ? <><Loader2 size={18} className="spin" /> Signing…</>
                        : <><Check size={18} /> Sign Document</>}
                </button>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(12px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                }
                .spin { animation: spin 1s linear infinite; display: inline-block; }
            `}</style>
        </div>
    );
}
